import React, { useMemo, useState } from "react";

/**
 * CharacterPortrait — circular portrait with a hand-drawn ink-wash ring
 * that adapts to the current aesthetic preset (reads CSS vars).
 *
 * Props:
 *   url        Portrait URL (relative or absolute). Prefixes REACT_APP_BACKEND_URL
 *              when the URL is relative.
 *   initial    Fallback letter when image is missing/broken.
 *   size       Diameter in px. Default 40.
 *   onClick    Optional tap handler — when provided, the portrait becomes a
 *              button with a pulse-on-tap feedback ring and a "Filter to X" tooltip
 *              (label prop).
 *   label      Accessible label + tooltip when onClick is set.
 *   active     Marks the portrait as the current filter target (thicker stroke).
 *   testid     Data-testid attribute.
 *
 * Notes:
 *  - The wobble uses a small SVG feTurbulence + feDisplacementMap filter.
 *  - Falls back to a clean dashed stroke if the filter isn't supported.
 *  - Kept subtle by design — the ring should feel like ink, not decoration.
 */
export default function CharacterPortrait({
  url,
  initial,
  size = 40,
  onClick,
  label,
  active = false,
  testid,
}) {
  const [broken, setBroken] = useState(false);
  const [tapping, setTapping] = useState(false);
  const BE = process.env.REACT_APP_BACKEND_URL || "";
  const src = url ? (url.startsWith("http") ? url : BE + url) : null;

  // Unique filter id per instance so multiple portraits don't clash
  const filterId = useMemo(
    () => `ink-wobble-${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  const ringThickness = Math.max(1, Math.round(size * 0.045));
  const wash = Math.max(3, Math.round(size * 0.12));
  const dashPattern = active ? "4 2 6 2 3 2" : "3 2 5 2 4 3";
  // Slight rotational offset makes the dash pattern look imperfect
  const dashOffset = active ? 0 : 2;

  const handleClick = onClick
    ? (e) => {
        e.stopPropagation();
        setTapping(true);
        setTimeout(() => setTapping(false), 220);
        onClick();
      }
    : undefined;

  const inner = (
    <span
      className="relative inline-flex items-center justify-center"
      style={{
        width: size,
        height: size,
        // Outer ink-wash: soft radial that fades outward. Adapts to theme.
        background:
          `radial-gradient(closest-side, transparent ${100 - Math.round((wash / size) * 100)}%, ` +
          `color-mix(in oklab, var(--sketchbook-wash, var(--primary-soft)) 55%, transparent) 100%)`,
        borderRadius: "9999px",
        padding: wash,
        transition: "transform 220ms cubic-bezier(0.4,0,0.2,1)",
        transform: tapping ? "scale(0.94)" : "scale(1)",
      }}
      data-testid={testid}
    >
      {/* SVG hand-drawn stroke overlay */}
      <svg
        aria-hidden
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: 0.9 }}
      >
        <defs>
          <filter id={filterId} x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.9"
              numOctaves="1"
              seed="4"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale={Math.max(0.8, size * 0.022)}
            />
          </filter>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={size / 2 - ringThickness / 2 - 1}
          fill="none"
          stroke="var(--sketchbook-stroke, color-mix(in oklab, var(--primary) 55%, transparent))"
          strokeWidth={active ? ringThickness + 0.5 : ringThickness}
          strokeDasharray={dashPattern}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          filter={`url(#${filterId})`}
        />
      </svg>

      {/* Actual portrait / initial */}
      <span
        className="relative w-full h-full rounded-full overflow-hidden flex items-center justify-center"
        style={{
          background: !src || broken
            ? "color-mix(in oklab, var(--accent) 20%, transparent)"
            : "transparent",
          color: "var(--accent)",
          fontFamily: "var(--font-display, serif)",
          fontSize: Math.round(size * 0.42),
          lineHeight: 1,
        }}
      >
        {src && !broken ? (
          <img
            src={src}
            alt=""
            onError={() => setBroken(true)}
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <span aria-hidden>{initial}</span>
        )}
      </span>

      {/* Tap-feedback outer pulse ring */}
      {tapping && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            boxShadow:
              "0 0 0 3px color-mix(in oklab, var(--primary) 45%, transparent)",
            transition: "opacity 220ms ease",
          }}
        />
      )}
    </span>
  );

  if (!onClick) return inner;

  return (
    <button
      type="button"
      onClick={handleClick}
      title={label}
      aria-label={label}
      className="rounded-full focus:outline-none focus-visible:ring-2"
      style={{ padding: 0, background: "transparent", border: "none", cursor: "pointer" }}
      data-testid={testid}
    >
      {inner}
    </button>
  );
}
